/**
 * Onboarding spotlight: lights one control, dims the rest and points the
 * arrow at it (docs/ONBOARDING_DESIGN_RULES.md W6, M2, M3).
 *
 * The element is the .onboard-layer from templates/macros/onboarding.html.
 * Everything is measured against the layer, not the viewport, so the same
 * code serves the app (a fixed layer over the screen) and /dev/cards (a layer
 * contained by a phone-sized frame). This controller only measures and
 * places; every timing is in static/css/onboarding.css.
 *
 * pointAt(target) lights a target, gliding from the previous one; an array
 * lights one cutout around them all, and a target that is gone or has no box
 * clears instead. veil() dims the screen with nothing lit; clear() hides it
 * all. While something is lit it follows scrolls, resizes, the on-screen
 * keyboard, the target's own size and any change to the page (a card
 * streamed in above it moves it without resizing it), coalesced to one
 * placement a frame. A stream can replace the lit control outright: `find`
 * names it again, and while it is gone the ring hides rather than sit over
 * whatever took its place.
 * Other controllers reach it as an outlet; the walkthrough's lives on a
 * permanent element, so connect() picks a lit target back up.
 */
import { Controller } from '@hotwired/stimulus';

import { spotlightFrame } from './_onboard.js';

export default class extends Controller {
    static targets = ['cutout', 'arrow'];

    // Made here, not in connect(): an outlet owner can call pointAt before
    // this controller's connect() has run, and pointAt listens with it.
    initialize() {
        this._onReflow = () => {
            if (this._frame) return;
            this._frame = requestAnimationFrame(() => {
                this._frame = null;
                this._place(false);
            });
        };
    }

    connect() {
        if (this._targets) this._listen();
        else this._targets = null;
    }

    disconnect() {
        this._unlisten();
    }

    /**
     * Light `target` (an element or a list of them). `shape: 'round'` cuts a
     * circle; `quiet` moves the arrow along without drawing it again (a fill
     * that walks field to field); `find` returns the control as it is on the
     * page now, for a target a stream may replace; `emphasis` is a control
     * that is easy to miss: a thicker ring that pulses, and the arrow nudging
     * again, a few rounds, then still (onboarding.css). Returns the frame, so
     * a caller can dock its card away from it, or null when there was
     * nothing to light.
     */
    pointAt(target, { shape = null, quiet = false, find = null, emphasis = false } = {}) {
        let targets = [target].flat().filter(Boolean);
        // Replaced between being found and lit (a save's stream): light the copy on the page now.
        if (find && !this._hasBox(targets)) targets = [find()].filter(Boolean);
        if (!targets.length || !this._hasBox(targets)) {
            this.clear();
            return null;
        }
        const moving = this._targets !== null;
        this._targets = targets;
        this._find = find;
        this._quiet = quiet;
        this._lost = false;
        this._listen();
        this._setShape(shape);
        this._setEmphasis(emphasis);
        delete this.cutoutTarget.dataset.veil;
        const frame = this._place(moving);
        if (quiet) this.arrowTarget.dataset.shown = 'quiet';
        else this._drawArrow(moving);
        this.cutoutTarget.dataset.shown = '';
        return frame;
    }

    /** Dim everything with nothing lit: a cutout of no size at the centre, no ring, no arrow. */
    veil() {
        this.clear();
        const cutout = this.cutoutTarget;
        const layer = this.element.getBoundingClientRect();
        cutout.dataset.placing = '';
        cutout.style.setProperty('--onboard-spot-x', `${layer.width / 2}px`);
        cutout.style.setProperty('--onboard-spot-y', `${layer.height / 2}px`);
        cutout.style.setProperty('--onboard-spot-w', '0px');
        cutout.style.setProperty('--onboard-spot-h', '0px');
        cutout.dataset.veil = '';
        cutout.getBoundingClientRect();
        delete cutout.dataset.placing;
        cutout.dataset.shown = '';
    }

    clear() {
        this._targets = null;
        this._find = null;
        this._unlisten();
        this._setEmphasis(false);
        delete this.cutoutTarget.dataset.shown;
        delete this.arrowTarget.dataset.shown;
    }

    _hasBox(targets) {
        return targets.some((el) => {
            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        });
    }

    _setShape(shape) {
        if (shape) this.cutoutTarget.dataset.shape = shape;
        else delete this.cutoutTarget.dataset.shape;
    }

    _setEmphasis(emphasis) {
        for (const part of [this.cutoutTarget, this.arrowTarget]) {
            if (emphasis) part.dataset.emphasis = '';
            else delete part.dataset.emphasis;
        }
    }

    /** Window listeners once while lit; the size watch moves to each new set of targets. */
    _listen() {
        if (!this._listening) {
            this._listening = true;
            window.addEventListener('resize', this._onReflow);
            window.addEventListener('scroll', this._onReflow, { capture: true, passive: true });
            window.visualViewport?.addEventListener('resize', this._onReflow);
            if (typeof ResizeObserver === 'function') this._observer = new ResizeObserver(this._onReflow);
            this._watchPage();
        }
        this._observer?.disconnect();
        this._targets.forEach((el) => this._observer?.observe(el));
    }

    /**
     * Any change to the page outside this layer: a control moved by what was
     * added above it, or replaced by a stream. The layer's own changes (the
     * cutout's position, the card) are left out, or placing would trigger itself.
     */
    _watchPage() {
        const body = globalThis.document?.body;
        if (!body || typeof MutationObserver !== 'function') return;
        this._pageObserver = new MutationObserver((records) => {
            if (records.some((record) => !this.element.contains?.(record.target))) this._onReflow();
        });
        this._pageObserver.observe(body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden'] });
    }

    _unlisten() {
        if (!this._listening) return;
        this._listening = false;
        window.removeEventListener('resize', this._onReflow);
        window.removeEventListener('scroll', this._onReflow, { capture: true });
        window.visualViewport?.removeEventListener('resize', this._onReflow);
        this._observer?.disconnect();
        this._observer = null;
        this._pageObserver?.disconnect();
        this._pageObserver = null;
        if (this._frame) cancelAnimationFrame(this._frame);
        this._frame = null;
    }

    /** One rect around every target that has a box. */
    _union() {
        const rects = this._targets.map((el) => el.getBoundingClientRect()).filter((r) => r.width && r.height);
        const left = Math.min(...rects.map((r) => r.left));
        const top = Math.min(...rects.map((r) => r.top));
        const right = Math.max(...rects.map((r) => r.left + r.width));
        const bottom = Math.max(...rects.map((r) => r.top + r.height));
        return { left, top, width: right - left, height: bottom - top };
    }

    /** A lit target a stream took off the page, named again by `find`, if it is back. */
    _refind() {
        if (!this._find || this._targets.every((el) => el.isConnected !== false)) return;
        const found = this._find();
        if (!found || this._targets.includes(found)) return;
        this._targets = [found];
        this._observer?.disconnect();
        this._observer?.observe(found);
    }

    /** The ring and arrow out while the lit control is gone, and back when it returns. */
    _setLost(lost) {
        if (Boolean(this._lost) === lost) return;
        this._lost = lost;
        if (lost) {
            delete this.cutoutTarget.dataset.shown;
            delete this.arrowTarget.dataset.shown;
        } else {
            this.cutoutTarget.dataset.shown = '';
            this.arrowTarget.dataset.shown = this._quiet ? 'quiet' : '';
        }
    }

    _place(animate) {
        if (!this._targets) return null;
        this._refind();
        if (!this._hasBox(this._targets)) {
            this._setLost(true);
            return null;
        }
        this._setLost(false);
        const radius = Number.parseFloat(getComputedStyle(this._targets[0]).borderTopLeftRadius) || 0;
        const frame = spotlightFrame(this._union(), this.element.getBoundingClientRect(), radius);
        const cutout = this.cutoutTarget;
        if (!animate) cutout.dataset.placing = '';
        cutout.style.setProperty('--onboard-spot-x', `${frame.x}px`);
        cutout.style.setProperty('--onboard-spot-y', `${frame.y}px`);
        cutout.style.setProperty('--onboard-spot-w', `${frame.w}px`);
        cutout.style.setProperty('--onboard-spot-h', `${frame.h}px`);
        cutout.style.setProperty('--onboard-spot-r', `${frame.r}px`);
        this.arrowTarget.dataset.side = frame.arrow.side;
        this.arrowTarget.style.setProperty('--onboard-arrow-x', `${frame.arrow.x}px`);
        this.arrowTarget.style.setProperty('--onboard-arrow-y', `${frame.arrow.y}px`);
        if (!animate) {
            cutout.getBoundingClientRect();
            delete cutout.dataset.placing;
        }
        return frame;
    }

    /** Restart the draw-in and nudges (M3); after a move, they wait for the cutout. */
    _drawArrow(afterMove) {
        const arrow = this.arrowTarget;
        delete arrow.dataset.shown;
        arrow.getBoundingClientRect();
        arrow.dataset.shown = afterMove ? 'after-move' : '';
    }
}
